import React from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  useModuleCompletionQuery,
  useMarkModuleCompleteMutation,
} from '../../../api/studentCoursesApi';
import { tokenService } from '../../../services/TokenService';

interface ModuleCompleteButtonProps {
  contextId: string;
  moduleId: string;
  /** 'floating' = sticky top-right overlay (default); 'footer' = inline for use inside a footer bar */
  variant?: 'floating' | 'footer';
}

/**
 * Sticky top-right "Mark as Complete" button shared by all module types.
 * Place this as the first child of the module's outermost content container.
 * Uses negative bottom margin so it takes up no layout space and floats over content.
 */
const ModuleCompleteButton: React.FC<ModuleCompleteButtonProps> = ({
  contextId,
  moduleId,
  variant = 'floating',
}) => {
  const token = tokenService.getToken();

  const { data: completionData } = useModuleCompletionQuery(
    contextId,
    moduleId,
    token,
  );

  const markComplete = useMarkModuleCompleteMutation(contextId);
  const isCompleted = completionData?.completed ?? false;

  const inner = isCompleted ? (
    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full shadow-sm">
      <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
      <span className="text-xs font-semibold text-green-700">Completed</span>
    </div>
  ) : (
    <Button
      size="sm"
      onClick={() => markComplete.mutate({ moduleId })}
      disabled={markComplete.isPending}
      className="bg-blue-600 hover:bg-blue-700 shadow-md rounded-full h-7 text-xs px-3 gap-1"
    >
      {markComplete.isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <CheckCircle2 className="h-3.5 w-3.5" />
      )}
      Mark as Complete
    </Button>
  );

  if (variant === 'footer') {
    return inner;
  }

  return (
    <div className="sticky top-2 z-20 flex justify-end pointer-events-none -mb-9 px-3 pt-1">
      <div className="pointer-events-auto">{inner}</div>
    </div>
  );
};

export default ModuleCompleteButton;
